import { useCallback, useEffect, useState } from 'react';
import debounce from 'lodash/debounce';

import useLocalStorage from '@lyearn/core/hooks/useLocalStorage';
import useToggle from '@lyearn/core/hooks/useToggle';
import { HeaderState } from '@/components/EditorViewer/hooks/types';
import useDefaultErrorSnackbar from '@/hooks/useDefaultErrorSnackbar';
import useIntersectionObserver from '@/hooks/useIntersectionObserver';
import useScrollProgress from '@/hooks/useScrollProgress';
import { useUserContext } from '@/modules/auth/hooks/useUser';
import { FieldType } from '@/modules/corporate/modules/database/interfaces';
import { PerformanceStatusEnum, UpsertDatabaseDataInput } from '@/types/schema';

import { useUpdateSurveyPerformanceMutation } from '../../../../graphql/mutations/generated/updateSurveyPerformance';
import parseSurveyFormValues from '../../utils/parseSurveyFormValues';

const LOCAL_SAVE_TIME = 150;

// isDisabled is added to prevent the user from submitting the form twice
// since the mutation only return success, the time between mutation is done and query is invalidated the user can submit the form again
// need to try -> send the complete survey is mutation response (already synced with @Paramjeet2810)
// meanwhile this is a temporary solution
const useSurveyContent = (
  surveyId: string,
  fields: FieldType[],
  setHeaderState?: (value: HeaderState) => void,
  isEmbed?: boolean,
) => {
  const user = useUserContext();
  const [bannerRef, setBannerRef] = useState<HTMLDivElement | null>(null);
  const [isDisabled, { on }] = useToggle(false);
  const [{ fetching }, updateSurveyPerformance] = useUpdateSurveyPerformanceMutation();

  const { showDefaultErrorSnackbar } = useDefaultErrorSnackbar();
  const { scrollProgress, direction } = useScrollProgress();
  const { isVisible: isBannerVisible } = useIntersectionObserver(bannerRef);
  const [initialValues, setInitialValues] = useLocalStorage<string>(surveyId, '{}');

  useEffect(() => {
    if (!setHeaderState) {
      return;
    }
    if (scrollProgress <= 0) {
      //in safari, scrollProgress can be negative
      setHeaderState(isBannerVisible ? HeaderState.Transparent : HeaderState.Standard);
      return;
    }
    switch (direction) {
      case 'down':
        setHeaderState(HeaderState.Hidden);
        break;
      default:
        setHeaderState(isBannerVisible ? HeaderState.Transparent : HeaderState.Standard);
    }
  }, [bannerRef, direction, isBannerVisible, scrollProgress, setHeaderState]);

  const onUpdateSurveyPerformance = useCallback(
    async (databaseDataInput: UpsertDatabaseDataInput, status?: PerformanceStatusEnum) => {
      const response = await updateSurveyPerformance({
        input: { userId: user.id, surveyId, status, databaseDataInput, isEmbed },
      });

      if (response.error) {
        showDefaultErrorSnackbar();
      } else if (response.data?.updateSurveyPerformance.success) {
        on();
      }

      return { response };
    },
    [isEmbed, on, showDefaultErrorSnackbar, surveyId, updateSurveyPerformance, user.id],
  );

  const onChangeValues = debounce(({ values }: any) => {
    if (values) {
      setInitialValues(JSON.stringify(parseSurveyFormValues({ values, fields }).initialValues));
    }
  }, LOCAL_SAVE_TIME);

  return {
    fetching: fetching || isDisabled,
    setBannerRef,
    onUpdateSurveyPerformance,
    onChangeValues,
    initialValues: JSON.parse(initialValues ?? '{}'),
  };
};

export default useSurveyContent;
